import { matchPath } from 'react-router-dom';
import navConfig from '../../layouts/dashboard/navbar/NavConfig';
import { rolesForRoute } from '../../constants/staffRoles';
import { NavListProps } from './type';

export * from './type';

// ----------------------------------------------------------------------

export { default as NavSectionVertical } from './vertical';
export { default as NavSectionHorizontal } from './horizontal';

const allowed = (item: { roles?: string[] }, userRoles: string[]) =>
  !item.roles || userRoles.some((r) => item.roles!.includes(r));

// Keeps the items one of the user's staff roles allows. A parent stays while any child
// is left, and then opens on its first child; a group with no items left is dropped.
export function filterNavConfig<T extends { items: NavListProps[] }>(
  config: T[],
  userRoles: string[]
): T[] {
  const filterItems = (items: NavListProps[]): NavListProps[] =>
    items.reduce((acc: NavListProps[], item) => {
      if (!allowed(item, userRoles)) return acc;
      if (!item.children) return [...acc, item];
      const children = filterItems(item.children);
      return children.length ? [...acc, { ...item, path: children[0].path, children }] : acc;
    }, []);

  return config
    .map((group) => ({ ...group, items: filterItems(group.items) }))
    .filter((group) => group.items.length > 0);
}

// Pages outside the menu (one record) and the API route that guards each (routes/index.tsx)
const RECORD_PAGES: [RegExp, string][] = [
  [
    /^\/dashboard\/partners\/(?!(all|pending|approved|rejected|suspended|deleted|location-requests)$)[^/]+$/,
    '/owner/partners/{partner_id}',
  ],
  [
    /^\/dashboard\/clients\/(?!(all|active|trial|expired|blocked|deleted)$)[^/]+$/,
    '/owner/clients/{admin_id}',
  ],
  [/^\/dashboard\/kyc\/review\/[^/]+$/, '/owner/partners/{partner_id}/kyc'],
  [/^\/dashboard\/upload-requests\/[^/]+$/, '/owner/upload-requests/{request_id}'],
  [/^\/dashboard\/announcements\/new$/, '/owner/announcements'],
];

// Whether a page opens for these roles: a record page by its API route, a menu page by its
// menu item (a path in neither does)
export function canOpenPath(path: string, userRoles: string[]): boolean {
  const record = RECORD_PAGES.find(([re]) => re.test(path));
  if (record) {
    const roles: string[] = rolesForRoute('GET', record[1]);
    return userRoles.some((r) => roles.includes(r));
  }

  const find = (items: NavListProps[]): NavListProps | undefined =>
    items.reduce<NavListProps | undefined>(
      (hit, item) =>
        hit || (item.children ? find(item.children) : item.path === path ? item : undefined),
      undefined
    );
  const found = navConfig.reduce<NavListProps | undefined>(
    (hit, group) => hit || find(group.items),
    undefined
  );
  return found ? allowed(found, userRoles) : true;
}

export function isExternalLink(path: string) {
  return path.includes('http');
}

export function getActive(path: string, pathname: string) {
  return path ? !!matchPath({ path: path, end: false }, pathname) : false;
}

const getAllNavPaths = (config: any[]): string[] => {
  const paths: string[] = [];
  const addItems = (items: any[]) => {
    items.forEach((item) => {
      if (item.path) paths.push(item.path);
      if (item.children) addItems(item.children);
    });
  };
  config.forEach((group) => {
    if (group.items) addItems(group.items);
  });
  return paths;
};

const ALL_NAV_PATHS = getAllNavPaths(navConfig);

export function isNavActive(path: string, pathname: string): boolean {
  if (!path) return false;
  if (pathname === path) return true;

  if (pathname.startsWith(path + '/')) {
    // Check if any other path in navConfig is an exact match for pathname
    // or is a longer prefix match than `path`
    const hasBetter = ALL_NAV_PATHS.some((p) => {
      if (p === path) return false;
      if (p === pathname) return true;
      if (pathname.startsWith(p + '/') && p.length > path.length) return true;
      return false;
    });

    return !hasBetter;
  }

  return false;
}
